import FetchClient, { type ApiResponse } from "./FetchClient";

export interface DollarGeneralCouponRule {
  offerId?: number;
  couponId?: string;
  offerCode?: string;
  issuer?: "dg_store" | "manufacturer";
  offerSourceType?: number;
  type: "spend_threshold" | "n_for_price" | "buy_get" | "amount_off_quantity" | "percent_off_quantity";
  summary?: string;
  brand?: string;
  description: string;
  expirationDate?: string;
  minimumSpendCents?: number;
  discountCents?: number;
  requiredQuantity?: number;
  bundlePriceCents?: number;
  buyQuantity?: number;
  getQuantity?: number;
  getDiscountPercent?: number;
}

export interface DollarGeneralCouponBasketLine {
  upc: string;
  name: string;
  image?: string;
  quantity: number;
  availableStock?: number;
  unitCents: number;
  totalCents: number;
}

export interface DollarGeneralCouponCombination {
  id: string;
  storeId: string;
  kind?: "same_item_stack" | "threshold_stack";
  title?: string;
  coupons: Array<{
    rule: DollarGeneralCouponRule;
    lines: DollarGeneralCouponBasketLine[];
    subtotalCents: number;
    discountCents: number;
    finalTotalCents: number;
    totalItems: number;
  }>;
  lines: DollarGeneralCouponBasketLine[];
  subtotalCents: number;
  discountCents: number;
  finalTotalCents: number;
  savingsPercent: number;
  totalItems: number;
  couponCount: number;
  earliestExpirationDate?: string;
}

export interface DollarGeneralCouponSnapshot {
  storeId: string;
  generatedAt: string;
  couponsScanned: number;
  productsWithOffers: number;
  source?: string;
  crawlResponseBytes?: number;
  crawlRequestCount?: number;
  couponFetchRequests?: number;
  combinations: DollarGeneralCouponCombination[];
}

export interface DollarGeneralCouponStoreSummary {
  storeId: string;
  available: boolean;
  comboCount: number;
  generatedAt?: string;
}

export interface DollarGeneralCouponCombosResponse {
  stores: DollarGeneralCouponStoreSummary[];
  snapshot?: DollarGeneralCouponSnapshot;
}

export function isDollarGeneralCouponCombination(value: unknown): value is DollarGeneralCouponCombination {
  if (!value || typeof value !== "object") return false;
  const combo = value as Partial<DollarGeneralCouponCombination>;
  return (
    typeof combo.id === "string" &&
    Array.isArray(combo.coupons) &&
    combo.coupons.length >= 2 &&
    combo.coupons.every((coupon) => Boolean(coupon?.rule) && Array.isArray(coupon?.lines)) &&
    Array.isArray(combo.lines) &&
    combo.lines.length > 0 &&
    combo.lines.every((line) =>
      typeof line?.quantity === "number" &&
      line.quantity > 0 &&
      typeof line.availableStock === "number" &&
      line.availableStock >= line.quantity
    ) &&
    typeof combo.couponCount === "number" &&
    combo.couponCount === combo.coupons.length
  );
}

export async function getDollarGeneralCouponCombos(storeId = "all") {
  const query = storeId ? `?storeId=${encodeURIComponent(storeId)}` : "";
  const response = await FetchClient.get<ApiResponse<DollarGeneralCouponCombosResponse>>(
    `/dollar-general/coupon-combos${query}`
  );
  return response.data;
}
